import React, { useMemo, useState } from "react";
import {
  Button,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Select,
  Space,
  Tooltip,
} from "antd";
import { FilterOutlined, ReloadOutlined } from "@ant-design/icons";

export type FilterItem =
  | { key: string; type: "text"; label: string; placeholder?: string }
  | {
      key: string;
      type: "select";
      label: string;
      options: { value: any; label: string }[];
      mode?: "multiple" | undefined;
      allowSearch?: boolean;
    }
  | {
      key: string;
      type: "numberRange";
      label: string;
      minPlaceholder?: string;
      maxPlaceholder?: string;
    }
  | { key: string; type: "dateRange"; label: string }
  | { key: string; type: "date"; label: string };

export type FilterItemConfig =
  | { key: string; type: "text"; label: string; placeholder?: string }
  | {
      key: string;
      type: "select";
      label: string;
      options: { value: any; label: string }[];
      mode?: "multiple";
      allowSearch?: boolean;
    }
  | {
      key: string;
      type: "numberRange";
      label: string;
      minPlaceholder?: string;
      maxPlaceholder?: string;
    }
  | { key: string; type: "dateRange"; label: string }
  | { key: string; type: "date"; label: string };

export interface Filtros {
  items: FilterItem[]; // always shown
  optionalItems?: FilterItem[]; // user can add/remove these
  value?: Record<string, any>;
  onChange?: (v: Record<string, any>) => void;
  onApply?: (v: Record<string, any>) => void;
  extra?: React.ReactNode; // place to the right of the buttons
  persistenceKey?: string; // <- unique key per section to persist filters between tab switches
  defaultOpen?: boolean;
}

/** ¿Este filtro trae algo puesto? Mismo criterio para contarlo y para mostrarlo. */
const tieneValor = (x: any) =>
  x != null && x !== "" && (!Array.isArray(x) || x.length > 0);

export function Filtros({
  items,
  optionalItems = [],
  value,
  onChange,
  onApply,
  extra,
  persistenceKey: _persistenceKey,
  defaultOpen,
}: Filtros) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [form] = Form.useForm();
  const [enabledOptionalKeys, setEnabledOptionalKeys] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false);

  // initialize from value
  React.useEffect(() => {
    if (!value) return;
    form.setFieldsValue(value);
    // Un filtro OPCIONAL recordado llega en `value`, pero su campo no está en
    // pantalla: `enabledOptionalKeys` arranca vacío. La tabla salía filtrada
    // por algo invisible y, al aplicar cualquier otro filtro, se perdía solo
    // (`getFieldsValue()` NO devuelve los campos que no están montados, aunque
    // el valor siga en el formulario). Así que se encienden los opcionales que
    // vengan con valor. Es aditivo: nunca apaga los que el admin agregó a mano
    // ni pisa lo que acaba de elegir.
    const conValor = optionalItems
      .filter((o) => tieneValor(value[o.key]))
      .map((o) => o.key);
    if (!conValor.length) return;
    setEnabledOptionalKeys((prev) => {
      const faltan = conValor.filter((k) => !prev.includes(k));
      return faltan.length ? [...prev, ...faltan] : prev;
    });
    // Solo `value`: `optionalItems` llega como arreglo nuevo en cada render y,
    // listado acá, volvería a pisar el formulario mientras el admin escribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const active = Form.useWatch([], form) as Record<string, any>;
  const activeCount = useMemo(
    () => Object.values(active || {}).filter(tieneValor).length,
    [active]
  );

  const emit = () => {
    const v = form.getFieldsValue();
    if (onApply) {
      onApply(v); // 👈 aquí enganchamos el fetch
    } else {
      onChange?.(v); // compatibilidad hacia atrás
    }

    setDirty(false);
  };

  const handleValuesChange = () => {
    const v = form.getFieldsValue();
    setDirty(true);
    onChange?.(v); // <- clave: actualiza el estado del padre en cada cambio
  };

  const clear = () => {
    form.resetFields();
    setEnabledOptionalKeys([]);
    setDirty(false);

    const empty = {};
    onChange?.(empty);
    onApply?.(empty);
  };

  return (
    <div className="smart-filter">
      <Space wrap>
        <Button icon={<FilterOutlined />} onClick={() => setOpen((v) => !v)}>
          Filtro avanzado{activeCount ? `: ${activeCount}` : ""}
        </Button>
        <Button type="primary" disabled={!dirty} onClick={() => form.submit()}>
          Aplicar filtros
        </Button>
        <Tooltip title="Limpiar filtros">
          <Button icon={<ReloadOutlined />} onClick={clear} />
        </Tooltip>
        {extra}
      </Space>

      {open && (
        <div className="smart-filter-panel">
          <Form
            form={form}
            layout="vertical"
            onValuesChange={handleValuesChange}
            onFinish={() => {
              if (!dirty) return; // mismo criterio que el botón
              emit(); // exactamente lo mismo que click
            }}
          >
            <button
              type="submit"
              style={{ display: "none" }}
              aria-hidden="true"
            />
            {optionalItems.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <span style={{ marginRight: 8, fontWeight: 500 }}>
                  Agregar filtros:
                </span>
                <Select
                  mode="multiple"
                  style={{ minWidth: 300 }}
                  placeholder="Selecciona filtros opcionales"
                  value={enabledOptionalKeys}
                  onChange={(v) => {
                    setEnabledOptionalKeys(v as string[]);
                    setDirty(true);
                  }}
                  options={optionalItems.map((o: any) => ({
                    value: o.key,
                    label: o.label,
                  }))}
                />
              </div>
            )}
            <div className="grid">
              {[
                ...items,
                ...optionalItems.filter((o: any) =>
                  enabledOptionalKeys.includes(o.key)
                ),
              ].map((it) => {
                if (it.type === "text")
                  return (
                    <Form.Item key={it.key} name={it.key} label={it.label}>
                      <Input placeholder={it.placeholder} allowClear />
                    </Form.Item>
                  );
                if (it.type === "select")
                  return (
                    <Form.Item key={it.key} name={it.key} label={it.label}>
                      <Select
                        showSearch
                        allowClear
                        mode={it.mode}
                        optionFilterProp="label"
                        options={(it as any).options}
                      />
                    </Form.Item>
                  );
                if (it.type === "numberRange")
                  return (
                    <Form.Item
                      key={it.key}
                      label={it.label}
                      style={{ marginBottom: 0 }}
                    >
                      <Space.Compact style={{ width: "100%" }}>
                        <Form.Item name={[it.key, "min"]} noStyle>
                          <InputNumber
                            placeholder={(it as any).minPlaceholder || "Mín"}
                            style={{ width: "50%" }}
                          />
                        </Form.Item>
                        <Form.Item name={[it.key, "max"]} noStyle>
                          <InputNumber
                            placeholder={(it as any).maxPlaceholder || "Máx"}
                            style={{ width: "50%" }}
                          />
                        </Form.Item>
                      </Space.Compact>
                    </Form.Item>
                  );
                if (it.type === "dateRange")
                  return (
                    <Form.Item key={it.key} name={it.key} label={it.label}>
                      <DatePicker.RangePicker
                        style={{ width: "100%" }}
                        format="DD-MM-YYYY"
                      />
                    </Form.Item>
                  );
                if (it.type === "date")
                  return (
                    <Form.Item key={it.key} name={it.key} label={it.label}>
                      <DatePicker.MonthPicker
                        style={{ width: "100%" }}
                        format="YYYY-MM"
                        placeholder="Seleccione un mes"
                      />
                    </Form.Item>
                  );
                return null;
              })}
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}
